/**
 * Search Infinite Scroll
 *
 * Automatically loads more search results as the user scrolls down the search page
 */
(function() {
  class SearchInfiniteScroll {
    constructor(container) {
      this.container = container;
      this.isLoading = false;
      this.hasMorePages = true;

      this.elements = {
        results: container.querySelector('.collection__results'),
        pagination: container.querySelector('.collection__pagination'),
        trigger: container.querySelector('.collection__infinite-scroll-trigger'),
        loading: container.querySelector('.collection__loading')
      };

      if (!this.elements.results || !this.elements.pagination || !this.elements.trigger) {
        console.warn('Search infinite scroll: Required elements not found');
        return;
      }

      this.init();
    }

    init() {
      // Create intersection observer for the trigger element
      this.observer = new IntersectionObserver(
        (entries) => this.handleIntersection(entries),
        {
          rootMargin: '800px', // Start loading 800px before the trigger is visible
          threshold: 0
        }
      );

      this.observer.observe(this.elements.trigger);
    }

    handleIntersection(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }

    async loadNextPage() {
      if (this.isLoading || !this.hasMorePages) return;

      // Get the next page URL from the pagination
      const nextLink = this.elements.pagination.querySelector('a[rel="next"]');

      if (!nextLink) {
        this.hasMorePages = false;
        this.elements.trigger.style.display = 'none';
        return;
      }

      const nextUrl = nextLink.href;
      this.isLoading = true;

      // Show loading indicator
      if (this.elements.loading) {
        this.elements.loading.style.display = 'block';
      }

      try {
        const response = await fetch(nextUrl);
        const html = await response.text();

        // Parse the HTML response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract the new products
        const newProducts = doc.querySelector('.collection__results product-list');
        const newPagination = doc.querySelector('.collection__pagination');

        if (newProducts) {
          // Get the current product list
          const currentProductList = this.elements.results.querySelector('product-list');

          if (currentProductList && newProducts.innerHTML.trim()) {
            // Append new products to the existing list
            const newProductItems = Array.from(newProducts.children);

            newProductItems.forEach((item, index) => {
              // Remove reveal-on-scroll attribute to prevent opacity: 0 issue
              item.removeAttribute('reveal-on-scroll');

              // Also remove from nested elements
              item.querySelectorAll('[reveal-on-scroll]').forEach(el => {
                el.removeAttribute('reveal-on-scroll');
              });

              // Append to DOM
              currentProductList.appendChild(item);

              // Animate the new item with a staggered delay
              // Using Web Animations API for smooth entrance
              const delay = index * 50; // 50ms stagger between items

              item.animate(
                [
                  { opacity: 0, transform: 'translateY(20px)' },
                  { opacity: 1, transform: 'translateY(0)' }
                ],
                {
                  duration: 400,
                  delay: delay,
                  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                  fill: 'both'
                }
              );
            });

            // Update the pagination
            if (newPagination) {
              this.elements.pagination.innerHTML = newPagination.innerHTML;
            } else {
              // No more pages
              this.hasMorePages = false;
              this.elements.trigger.style.display = 'none';
            }

            // Trigger custom event for other scripts that might need to know about new products
            document.dispatchEvent(new CustomEvent('search:products-loaded', {
              detail: { products: newProductItems }
            }));
          }
        }
      } catch (error) {
        console.error('Error loading next page:', error);
        this.hasMorePages = false;
      } finally {
        this.isLoading = false;

        // Hide loading indicator
        if (this.elements.loading) {
          this.elements.loading.style.display = 'none';
        }
      }
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  // Initialize infinite scroll when DOM is ready
  function initInfiniteScroll() {
    const searchPanel = document.querySelector('search-result-panel[data-infinite-scroll="true"]');

    if (searchPanel) {
      new SearchInfiniteScroll(searchPanel);
    }
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll);
  } else {
    initInfiniteScroll();
  }

  // Re-initialize after facet changes (for live filtering/sorting)
  document.addEventListener('facet:updated', () => {
    // Small delay to ensure DOM is updated
    setTimeout(initInfiniteScroll, 100);
  });
})();
